import moment from 'moment';
import '../../bins/parts/bin-path-name';
import '../../common/bn-modal';
import '../../common/bn-modal-button';
import '../../common/bn-loading';
import '../../common/bn-property';
import '../../common/bn-action';
import '../../common/bn-icon-bin';
import './work-order-review-device';
import './work-order-review-bin';
import '../../devices/parts/device-monitoring-chart';
import '../../work-order-admin/work-order-admin-view';
import './reject-work-order-modal';
import { LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

class WorkOrderReviewListItem extends LitEntity {
  static get properties() {
    return {
      binHref: { type: String, attribute: false },
      deviceHref: { type: String, attribute: false },
      completedByUserHref: { type: String, attribute: false },
      hide: { type: Boolean, reflect: true },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
        max-height: 1500px;
        transition: opacity .2s, max-height .4s, margin .4s;
      }
      :host([hide]) {
        opacity: 0;
        max-height: 0px;
        margin: 0;
        pointer-events: none;
      }
      .header {
        display: flex;
        width: 100%;
        justify-content: space-between;
        margin-bottom: 1em;
        flex-direction: row;
      }
      .header bn-property {
        margin: 0;
      }
      .location {
        display: flex;
      }
      bn-icon-bin {
        height: 6em;
        width: 3em;
        margin-right: 1em;
        margin-left: .5em;
      }
      .bin-name {
        display: inline-block;
        flex-direction: row;
        margin-bottom: .5em;
        --bin-link-color: var(--blue-50);
      }
      .bin-name bn-property {
        font-weight: bold;
      }
      .body {
        display: grid;
        flex-wrap: wrap;
        grid-template-columns: 1fr 2fr;
        grid-gap: 1em;
      }
      .body .well {
        padding: 1em;
        display: flex;
        flex-direction: column;
      }
      .body .well h4 {
        margin-top: 0;
      }
      .body .well .info {
        font-size: .9em;
        flex-grow: 1;
      }
      .body .device {
        flex-grow: 1;
      }
      .body .bin {
        flex-grow: 2;
      }
      .body .readings {
        grid-column: 1 / 3;
      }
      bn-property {
        margin: .5em 2em 0 0;
        white-space: unset;
      }
      device-monitoring-chart {
        margin: 0;
      }
      .actions {
        display: flex;
        align-items: flex-start;
        justify-content: flex-end;
      }
      .actions > * {
        white-space: nowrap;
        margin: 0 0 0 .5em;
      }
      @media(max-width: 1200px) {
        .body .well {
          grid-column: 1 / 3;
        }
        bn-property + bn-property {
          margin: .5em 0 0 0;
        }
        .actions {
          flex-direction: column;
          align-items: flex-end;
          justify-content: flex-start;
        }
        .actions > * {
          margin: 0 0 .5em 0;
        }
      }
    `];
  }

  render() {
    if (this.loading) {
      return html`
        <bn-surface>
          <bn-loading></bn-loading>
        </bn-surface>
      `;
    }

    return html`
      <bn-surface class="work-order">
        <div class="header">
          ${this._renderLocation()}
          ${this._renderActions()}
        </div>

        <div class="body">
          ${this._renderDevice()}
          ${this._renderBin()}
          ${this._renderReadings()}
        </div>
      </bn-surface>
    `;
  }

  _renderLocation() {
    const binHref = this.getHref('https://api.binsentry.com/rel/bin');
    const { location, completedAt, requestType } = (this.entity && this.entity.properties) || {};
    const requestTypeText = requestType.charAt(0).toUpperCase() + requestType.slice(1);

    return html`
      <div class="location">
        <bn-icon-bin></bn-icon-bin>
        <div class="info">
          <div class="bin-name">
            <bn-property stacked label="${requestTypeText} of">
              <bin-path-name .href="${binHref}" bin-link-path="/bins/show/overview"></bin-path-name>
            </bn-property>
            ${(location || {}).address}
          </div>

          ${renderIf(this.completedByUserHref, () => html`
            <bn-property stacked label="${requestTypeText} by">
              <organization-name .href="${this.completedByUserHref}"></organization-name>
              on
              ${this._formatDateTime(completedAt)}
            </bn-property>
          `)}
        </div>
      </div>
    `;
  }

  _renderActions() {
    const actions = [html`
      <bn-modal-button button-class="button--tertiary">
        <span slot="text">Work Order Details</span>
        <bn-modal slot="modal">
          <span slot="title">Work Order</span>
          <work-order-admin-view .href=${this.href}></work-order-admin-view>
        </bn-modal>
      </bn-modal-button>
    `];

    if (this.entity.hasAction('accept-work-order')) {
      actions.push(html`
        <bn-action
          .href="${this.href}"
          suppressheader
          actionname="accept-work-order"
          submittext="Accept"
          @response="${this._onResponse}"
        ></bn-action>
      `);
    }

    if (this.entity.hasAction('reject-work-order')) {
      actions.push(html`
        <bn-modal-button button-class="button--containedError">
          <reject-work-order-modal
            .href="${this.href}"
            slot="modal"
            @response="${this._onResponse}"
          ></reject-work-order-modal>
          <span slot="text">Reject</span>
        </bn-modal-button>
      `);
    }

    return html`<div class="actions">${actions}</div>`;
  }

  _renderDevice() {
    return html`
      <div class="device well">
        <h4>Device</h4>
        <work-order-review-device
          .href="${this.deviceHref}"
          class="info"
        ></work-order-review-device>
      </div>
    `;
  }

  _renderBin() {
    const binHref = this.getHref('https://api.binsentry.com/rel/bin');

    return html`
      <div class="bin well">
        <h4>Bin</h4>
        <work-order-review-bin
          .href="${binHref}"
          class="info"
        ></work-order-review-bin>
      </div>
    `;
  }

  _renderReadings() {
    return html`
      <div class="readings well">
        <h4>Readings</h4>
        <device-monitoring-chart
          visible-layers="${JSON.stringify(['levels', 'battery', 'cell', 'batteryTemperature'])}"
          .href="${this.deviceHref}"
        ></device-monitoring-chart>
      </div>
    `;
  }

  _formatDateTime(date) {
    return moment(date).format('MMM D, h:mm a');
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.binHref = this.getHref('https://api.binsentry.com/rel/bin');
    this.completedByUserHref = this.getHref('https://api.binsentry.com/rel/work-order-completed-by');
    const deviceEntityLink = this.entity && this.entity.getSubEntityByClasses(['device', 'active']);
    this.deviceHref = (deviceEntityLink || {}).href;
  }

  _onResponse() {
    this.hide = true;
  }
}

customElements.define('work-order-review-list-item', WorkOrderReviewListItem);
